import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../config/apiService';

// SIP sites (same list/rationale as the qual + drawing managers).
const SIP_SITES = [
    { siteID: 5, site: 'CW Valencia 1 & 3', code: 'VAL01' },
    { siteID: 6, site: 'CW Valencia 2', code: 'VAL02' },
    { siteID: 3, site: 'CW Sacramento', code: 'SAC05' },
];

const TABS = [
    { key: 'picklist', label: 'Pick List' },
    { key: 'packing',  label: 'Packing Slips' },
    { key: 'summary',  label: 'Order Summary' },
];

const fullName = (r) => `${r.firstName} ${r.lastName}`.trim();
const monthLabel = (y, m) => new Date(y, m - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });

// Order Processing. Orders freeze onto a period at the cutoff, so every report
// is scoped to a month (or "Current" = the still-open cart).
//  - Pick List      : what to pull, per BOX, with check-off that decrements the box
//  - Packing Slips  : one slip per supervisor (confirmation of what's included)
//  - Order Summary  : the month's orders, by site and overall
function OrderProcessingManager() {
    const access = (localStorage.getItem('admin_access_visibility') || 'ALL-ALL').toUpperCase();
    const myCode = access.split('-')[1] || 'ALL';
    const visibleSites = myCode === 'ALL' ? SIP_SITES : SIP_SITES.filter((s) => s.code === myCode);
    const isGlobal = myCode === 'ALL';

    const [tab, setTab] = useState('picklist');
    const [months, setMonths] = useState([]);
    const [sel, setSel] = useState('current');          // 'current' | 'YYYY-MM'
    const [siteID, setSiteID] = useState(isGlobal ? 0 : (visibleSites[0]?.siteID || 0));
    const [lines, setLines] = useState([]);
    const [picks, setPicks] = useState([]);
    const [picked, setPicked] = useState({});           // `${invId}-${boxId}` -> true
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    const period = useMemo(() => {
        if (sel === 'current') return {};
        const [y, m] = sel.split('-').map(Number);
        return { year: y, month: m };
    }, [sel]);

    useEffect(() => {
        adminAPI.getOrderMonths().then((r) => setMonths(r.data || [])).catch(() => setMonths([]));
    }, []);

    const reload = async () => {
        setLoading(true); setErr('');
        try {
            const args = { ...period, siteId: siteID };
            if (tab === 'picklist') {
                const r = await adminAPI.getPickList(args);
                setPicks(r.data || []); setPicked({});
            } else {
                const r = await adminAPI.getOrderLines(args);
                setLines(r.data || []);
            }
        } catch (e) {
            setErr(e?.response?.data || e?.message || 'Failed to load orders');
        } finally { setLoading(false); }
    };

    useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab, sel, siteID]);

    const confirmPick = async (p) => {
        const key = `${p.sipSwagItemInventoryID}-${p.sipBoxID}`;
        setBusy(true); setErr('');
        try {
            await adminAPI.pickConfirm({ inventoryId: p.sipSwagItemInventoryID, boxId: p.sipBoxID, quantity: p.pullQty });
            setPicked((prev) => ({ ...prev, [key]: true }));
        } catch (e) {
            setErr(e?.response?.data?.message || e?.response?.data || e?.message || 'Pick confirm failed');
        } finally { setBusy(false); }
    };

    // packing slips: group lines by supervisor
    const slips = useMemo(() => {
        const map = new Map();
        for (const r of lines) {
            const k = r.supervisorEmployeeID;
            if (!map.has(k)) map.set(k, { id: k, name: fullName(r), site: r.site, lines: [] });
            map.get(k).lines.push(r);
        }
        return [...map.values()].sort((a, b) => a.site.localeCompare(b.site) || a.name.localeCompare(b.name));
    }, [lines]);

    // summary: per site -> item/size totals
    const summary = useMemo(() => {
        const bySite = new Map();
        for (const r of lines) {
            if (!bySite.has(r.site)) bySite.set(r.site, new Map());
            const m = bySite.get(r.site);
            const k = `${r.itemDescription}|||${r.sizeName}`;
            const cur = m.get(k) || { item: r.itemDescription, size: r.sizeName, qty: 0, points: 0 };
            cur.qty += r.itemQuantity; cur.points += r.pointsUsed;
            m.set(k, cur);
        }
        return [...bySite.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([site, m]) => {
            const items = [...m.values()].sort((a, b) => a.item.localeCompare(b.item) || a.size.localeCompare(b.size));
            return { site, items, qty: items.reduce((s, x) => s + x.qty, 0), points: items.reduce((s, x) => s + x.points, 0) };
        });
    }, [lines]);

    const totalUnits = lines.reduce((s, r) => s + r.itemQuantity, 0);
    const pickUnits = picks.reduce((s, p) => s + p.pullQty, 0);
    const active = TABS.find((t) => t.key === tab);
    const periodLabel = sel === 'current' ? 'Current (open) orders' : monthLabel(period.year, period.month);
    const siteLabel = siteID === 0 ? 'All sites' : (SIP_SITES.find((s) => s.siteID === siteID)?.site || '');

    return (
        <div style={styles.wrap}>
            <style>{PRINT_CSS}</style>

            <div className="op-noprint">
                <div style={styles.eyebrow}>Settings</div>
                <h2 style={styles.h2}>Order Processing</h2>
                <p style={styles.muted}>
                    Orders freeze onto their period at the cutoff, so each report is for a month
                    (or the current open window).
                </p>

                <div style={styles.tabBar}>
                    {TABS.map((t) => (
                        <button key={t.key} onClick={() => setTab(t.key)} style={{ ...styles.tab, ...(tab === t.key ? styles.tabActive : {}) }}>
                            {t.label}
                        </button>
                    ))}
                </div>

                <div style={styles.controls}>
                    <label style={styles.label}>Month</label>
                    <select value={sel} onChange={(e) => setSel(e.target.value)} style={styles.input}>
                        <option value="current">Current (open)</option>
                        {months.map((m) => (
                            <option key={m.sipDisbursementDateID} value={`${m.year}-${m.month}`}>
                                {monthLabel(m.year, m.month)} ({m.orderLines} lines)
                            </option>
                        ))}
                    </select>

                    <label style={styles.label}>Site</label>
                    <select value={siteID} onChange={(e) => setSiteID(Number(e.target.value))} style={styles.input}>
                        {isGlobal && <option value={0}>All sites</option>}
                        {visibleSites.map((s) => <option key={s.siteID} value={s.siteID}>{s.site}</option>)}
                    </select>

                    <div style={{ flex: 1 }} />
                    <button onClick={() => window.print()} disabled={loading} style={btn('#2563eb', loading)}>
                        🖨 Print {active.label}
                    </button>
                </div>

                {err && <div style={styles.error}>{String(err)}</div>}
            </div>

            <div className="op-print">
                <div className="op-doc-head" style={styles.printHeader}>
                    <div style={{ fontSize: 18, fontWeight: 800 }}>Safety Incentive Program — {active.label}</div>
                    <div style={styles.muted}>
                        {siteLabel} · {periodLabel}
                        <span className="op-printed"> · Printed {new Date().toLocaleDateString('en-US')}</span>
                    </div>
                </div>

                {loading ? (
                    <div style={styles.placeholder}>Loading…</div>
                ) : tab === 'picklist' ? (
                    picks.length === 0 ? <div style={styles.placeholder}>Nothing to pick for this selection.</div> : (
                        <>
                            <div style={styles.muted}>{pickUnits} units across {picks.length} box pulls — grouped by box.</div>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={{ ...styles.th, width: 60 }}>Box</th>
                                        <th style={styles.th}>Item</th>
                                        <th style={styles.th}>Size</th>
                                        <th style={{ ...styles.th, textAlign: 'right' }}>Pull</th>
                                        <th style={{ ...styles.th, textAlign: 'right' }}>In box</th>
                                        <th style={{ ...styles.th, width: 90 }}>Done</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {picks.map((p) => {
                                        const key = `${p.sipSwagItemInventoryID}-${p.sipBoxID}`;
                                        const done = !!picked[key];
                                        return (
                                            <tr key={key} style={{ background: done ? '#f0fdf4' : 'transparent' }}>
                                                <td style={{ ...styles.td, fontWeight: 800 }}>{p.boxCode}</td>
                                                <td style={styles.td}>{p.itemDescription}</td>
                                                <td style={styles.td}>{p.sizeName || '—'}</td>
                                                <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>{p.pullQty}</td>
                                                <td style={{ ...styles.td, textAlign: 'right', color: '#6b7280' }}>{p.boxQty}</td>
                                                <td style={styles.td}>
                                                    <span className="op-print-box">☐</span>
                                                    <button
                                                        className="op-noprint"
                                                        onClick={() => confirmPick(p)}
                                                        disabled={busy || done}
                                                        style={btn(done ? '#9ca3af' : '#15803d', busy || done)}
                                                    >
                                                        {done ? '✓ Picked' : 'Pick'}
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </>
                    )
                ) : tab === 'packing' ? (
                    slips.length === 0 ? <div style={styles.placeholder}>No orders for this selection.</div> : (
                        <div>
                            {slips.map((s) => (
                                <div key={s.id} className="op-slip" style={styles.slip}>
                                    <div style={styles.slipHead}>
                                        <div style={{ fontSize: 15, fontWeight: 800 }}>
                                            {s.name} <span style={{ color: '#9ca3af', fontWeight: 400, fontSize: 12 }}>#{s.id}</span>
                                        </div>
                                        <div style={styles.muted}>{s.site} · {periodLabel}</div>
                                    </div>
                                    <table style={styles.table}>
                                        <thead><tr><th style={styles.th}>Item</th><th style={styles.th}>Size</th><th style={{ ...styles.th, textAlign: 'right' }}>Qty</th><th style={{ ...styles.th, textAlign: 'right' }}>Points</th></tr></thead>
                                        <tbody>
                                            {s.lines.map((l, i) => (
                                                <tr key={i}>
                                                    <td style={styles.td}>{l.itemDescription}</td>
                                                    <td style={styles.td}>{l.sizeName || '—'}</td>
                                                    <td style={{ ...styles.td, textAlign: 'right' }}>{l.itemQuantity}</td>
                                                    <td style={{ ...styles.td, textAlign: 'right' }}>{l.pointsUsed}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ))}
                        </div>
                    )
                ) : (
                    summary.length === 0 ? <div style={styles.placeholder}>No orders for this selection.</div> : (
                        <div>
                            {summary.map((g) => (
                                <div key={g.site} style={{ marginBottom: 18 }}>
                                    <div style={styles.summarySite}>
                                        {g.site} <span style={{ color: '#6b7280', fontWeight: 400, fontSize: 12 }}>· {g.qty} units · {g.points} pts</span>
                                    </div>
                                    <table style={styles.table}>
                                        <thead><tr><th style={styles.th}>Item</th><th style={styles.th}>Size</th><th style={{ ...styles.th, textAlign: 'right' }}>Qty</th><th style={{ ...styles.th, textAlign: 'right' }}>Points</th></tr></thead>
                                        <tbody>
                                            {g.items.map((it, i) => (
                                                <tr key={i}>
                                                    <td style={styles.td}>{it.item}</td>
                                                    <td style={styles.td}>{it.size || '—'}</td>
                                                    <td style={{ ...styles.td, textAlign: 'right' }}>{it.qty}</td>
                                                    <td style={{ ...styles.td, textAlign: 'right' }}>{it.points}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ))}
                            <div style={styles.grandTotal}>
                                Grand total: {totalUnits} units{summary.length > 1 ? ` across ${summary.length} sites` : ''}
                            </div>
                        </div>
                    )
                )}
            </div>
        </div>
    );
}

const btn = (bg, disabled) => ({
    background: disabled ? '#cbd5e1' : bg, color: 'white', border: 'none', borderRadius: 6,
    padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: disabled ? 'default' : 'pointer',
});

const styles = {
    wrap: { padding: 24, maxWidth: 1000 },
    eyebrow: { fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: '#9ca3af', fontWeight: 700 },
    h2: { margin: '2px 0 4px', fontSize: 22, fontWeight: 800, color: '#111827' },
    muted: { color: '#6b7280', fontSize: 13, lineHeight: 1.5, margin: '4px 0' },
    tabBar: { display: 'flex', gap: 4, borderBottom: '1px solid #e5e7eb', marginTop: 16 },
    tab: { background: 'none', border: 'none', borderBottom: '2px solid transparent', padding: '8px 14px', fontSize: 14, fontWeight: 600, color: '#6b7280', cursor: 'pointer' },
    tabActive: { color: '#2563eb', borderBottomColor: '#2563eb' },
    controls: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 16, flexWrap: 'wrap' },
    label: { fontSize: 13, fontWeight: 600, color: '#374151' },
    input: { padding: '6px 10px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6, minWidth: 150 },
    error: { marginTop: 12, padding: 10, background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 6, fontSize: 13 },
    printHeader: { margin: '18px 0 10px' },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: 6 },
    th: { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#6b7280', borderBottom: '2px solid #e5e7eb', padding: '6px 8px' },
    td: { fontSize: 13, color: '#111827', borderBottom: '1px solid #f3f4f6', padding: '6px 8px' },
    slip: { border: '1px solid #e5e7eb', borderRadius: 8, padding: 14, marginBottom: 12 },
    slipHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderBottom: '1px solid #f3f4f6', paddingBottom: 6 },
    summarySite: { fontSize: 14, fontWeight: 800, color: '#111827', borderBottom: '2px solid #111827', paddingBottom: 4 },
    grandTotal: { marginTop: 8, fontSize: 14, fontWeight: 800, color: '#111827' },
    placeholder: { marginTop: 12, padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13, border: '1px dashed #d1d5db', borderRadius: 8 },
};

// Screen shows the Pick buttons; print shows a tick box instead. One slip per page.
// (The nav / Sign out chrome is hidden by the shared rules in AdminLayout.)
const PRINT_CSS = `
.op-print-box { display: none; }
.op-printed { display: none; }
@media print {
    .op-noprint { display: none !important; }
    .op-print-box { display: inline; font-size: 18px; }
    .op-printed { display: inline; }
    .op-doc-head { border-bottom: 2px solid #111827; padding-bottom: 6px; margin-bottom: 12px; }
    .op-slip { page-break-inside: avoid; break-inside: avoid; border: none !important; padding: 0 !important; }
    .op-slip + .op-slip { page-break-before: always; }
    thead { display: table-header-group; }   /* repeat headers across pages */
    tr { page-break-inside: avoid; }
    table { width: 100%; border-collapse: collapse; }
    th, td { font-size: 12px; }
}`;

export default OrderProcessingManager;
